import { useCallback, useEffect, useRef, useState } from 'react';
import { alert } from 'equasion-lib';
import { buzz, playInfoTone } from '@/lib/scanning/feedback';
import {
  combineAlertIds,
  combinedToast,
  soLabel,
  type CombineContext,
  type CombineOrder
} from '@/lib/combineShipment';
import useCombineShipment, { type CombineActionResult } from './useCombineShipment';

/**
 * The combine step's data AND its on-screen state, for one Pick or Verify screen:
 *
 *  - the one-time ALERT SHEET: opened when the alert is `armed` (Pick: the pick path is finished;
 *    Verify: the order is open for scanning) and the context names an order the operator has not
 *    been told about yet. Dismissed, it stays dismissed for those orders on this screen — a poll
 *    re-opens it only for a NEW order of the client;
 *  - the pick-ticket SCANNER overlay;
 *  - combine / remove, each answered with a toast.
 */
export function useCombineStep({
  companyId,
  salesOrderId,
  enabled = true,
  armed
}: {
  companyId: string;
  salesOrderId: string;
  enabled?: boolean;
  /** The alert may interrupt now. */
  armed: boolean;
}) {
  const data = useCombineShipment({ companyId, salesOrderId, enabled });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const seenRef = useRef<Set<string>>(new Set());
  const alertKey = data.alertIds.join(',');

  /** Whether `context`'s alert names an order the operator has not been told about yet. */
  const unseenIn = useCallback(
    (context: CombineContext | null | undefined) =>
      combineAlertIds(context).some((id) => !seenRef.current.has(id)),
    []
  );
  const unseenAlert = useCallback(
    () => data.alertIds.some((id) => !seenRef.current.has(id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertKey]
  );

  const openSheet = useCallback(() => {
    setSheetOpen(true);
    playInfoTone();
    buzz([60, 80, 60]);
  }, []);

  useEffect(() => {
    if (!armed || scanOpen || sheetOpen) return;
    if (unseenAlert()) openSheet();
  }, [armed, scanOpen, sheetOpen, unseenAlert, openSheet]);

  /** The operator has read the alert: mark its orders seen and close the sheet. */
  const acknowledge = useCallback(() => {
    data.alertIds.forEach((id) => seenRef.current.add(id));
    setSheetOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertKey]);

  const openScan = useCallback(() => {
    acknowledge();
    setScanOpen(true);
  }, [acknowledge]);
  const closeScan = useCallback(() => setScanOpen(false), []);

  /** From the scanner: the read / typed code. The overlay shows a refusal itself. */
  const combineCode = useCallback(
    async (code: string): Promise<CombineActionResult> => {
      const result = await data.combine({ code });
      if (result.ok) {
        const toast = combinedToast(result);
        alert(toast.title, toast.text, 5, 'success');
      }
      return result;
    },
    [data]
  );

  /** From an alert row: combine that order with one tap. */
  const combineOrder = useCallback(
    async (order: CombineOrder) => {
      acknowledge();
      const result = await data.combine({ orderId: order.id });
      if (result.ok) {
        const toast = combinedToast(result);
        alert(toast.title, toast.text, 5, 'success');
      } else {
        alert('Not combined', result.message, 8, 'error');
      }
    },
    [acknowledge, data]
  );

  const removeOrder = useCallback(
    async (order: CombineOrder) => {
      const result = await data.remove(order.id);
      if (result.ok) {
        alert(
          'Removed from shipment',
          order.isCurrent
            ? 'This order is verified and shipped on its own.'
            : `${soLabel(order)} is verified and shipped on its own.`,
          5,
          'success'
        );
      } else {
        alert('Could not remove', result.message, 8, 'error');
      }
    },
    [data]
  );

  return {
    ...data,
    sheetOpen,
    scanOpen,
    /** An order of the alert the operator has not been told about yet. */
    unseenAlert,
    unseenIn,
    openSheet,
    acknowledge,
    openScan,
    closeScan,
    combineCode,
    combineOrder,
    removeOrder
  };
}

export type CombineStep = ReturnType<typeof useCombineStep>;

export default useCombineStep;
